import * as React from 'react'

import { ChevronDownIcon, RotateCcwIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

interface IProps {
  createNewGlobe: () => Promise<void>
  saveSelectedGlobe: () => Promise<void>
  isSavingGlobe: boolean
  isResetDisabled: boolean
  resetViewport: () => void
  children?: React.ReactNode

  state?: DesktopInitialState
}

// A disabled button is half transparent; a busy split button needs a solid ground over the map, or
// the map shows through. An enabled one needs none: a ground would show at its rounded corners and
// above it as it is pressed down.
const SOLID_GROUND = 'tw:flex tw:rounded-md tw:bg-background'

// An outline button that stays solid while disabled, so it needs no ground.
const MUTED_WHEN_DISABLED =
  'tw:disabled:text-muted-foreground tw:disabled:opacity-100'

// A primary button's fill stops inside its transparent border; on the solid ground that border
// would show as a white edge, so it takes the fill's colour.
const PRIMARY_EDGE = 'tw:border-primary'

// The halves part on a short rule inside the menu half, not on a border, which the ground would show
// through: light on the primary fill, the border's colour when quiet, whose seam border is dropped.
const DIVIDER =
  'tw:relative tw:before:absolute tw:before:inset-y-2 tw:before:left-0 tw:before:w-px'
const PRIMARY_DIVIDER = `${PRIMARY_EDGE} tw:before:bg-primary-foreground/40`
const QUIET_DIVIDER = 'tw:border-l-0 tw:before:bg-border'

// The view's own actions, level with the view pill: one height, one radius, each on a solid
// ground over the map. Save view and its menu read as one split button.
const GlobeLimitedRibbon: React.FC<IProps> = ({
  createNewGlobe,
  saveSelectedGlobe,
  state,
  isSavingGlobe,
  resetViewport,
  isResetDisabled,
  children,
}) => {
  const { translator } = Localization
  const isSaving = isSavingGlobe || !!state.loading.get(e.SAVE_GLOBE_VIEW)
  const isSaveDisabled = isResetDisabled || isSaving
  // Nothing to save: the split button goes quiet as a whole, its menu still open for a new view.
  const isQuiet = isResetDisabled

  return (
    <div className="tw:absolute tw:top-0 tw:right-2.5 tw:flex tw:h-12 tw:items-center tw:gap-2">
      {children}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="outline"
              size="icon"
              aria-label={translator.restoreMapView}
              className={MUTED_WHEN_DISABLED}
              disabled={isResetDisabled}
              onClick={resetViewport}
            />
          }
        >
          <RotateCcwIcon />
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {translator.restoreMapView}
        </TooltipContent>
      </Tooltip>
      <div
        className={classList({
          'tw:flex': true,
          [SOLID_GROUND]: isQuiet || isSaving,
        })}
      >
        <Button
          variant={isQuiet ? 'outline' : 'default'}
          className={classList({
            'tw:rounded-r-none tw:border-r-0': true,
            [MUTED_WHEN_DISABLED]: isQuiet,
            [PRIMARY_EDGE]: !isQuiet,
          })}
          disabled={isSaveDisabled}
          onClick={saveSelectedGlobe}
        >
          {isSaving && (
            <span aria-hidden className="tw:flex">
              <Spinner size="sm" variant={isQuiet ? 'dark' : 'white'} />
            </span>
          )}
          {translator.saveMapView}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant={isQuiet ? 'outline' : 'default'}
                size="icon"
                aria-label={translator.moreSaveOptions}
                className={classList({
                  [`tw:rounded-l-none ${DIVIDER}`]: true,
                  [QUIET_DIVIDER]: isQuiet,
                  [PRIMARY_DIVIDER]: !isQuiet,
                })}
                disabled={isSaving}
              />
            }
          >
            <ChevronDownIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="tw:w-auto">
            <DropdownMenuItem onClick={createNewGlobe}>
              {translator.saveAsNewMapView}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export default inject('state')(observer(GlobeLimitedRibbon))
